import cn from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DownloadIcon from '../../../assets/images/icon-download.svg';
import { ReactComponent as DownloadIconV2 } from '../../../assets/images/icon-downloadV2.svg';
import { DropdownWithContent } from '../../../components';
import { AsyncButton, TextIconButton } from '../../../components/forms';
import { TableSearchInput as ExternalInput } from '../../../components/_mui';
import { MAX_CAMPAIGN_ITEMS_PER_PAGE } from '../../../constants/campaigns';
import { NO_FULFILLMENT_CENTER } from '../../../constants/fulfillmentCenters';
import { MAX_INVENTORY_ITEMS_PER_PAGE } from '../../../constants/inventories';
import { REGULAR_SEND_STATUSES, SCHEDULED_SEND_STATUSES, SEND_TYPES } from '../../../constants/reports';
import { INITIAL_PAGE } from '../../../constants/table';
import { useHeaderDispatch } from '../../../contexts/HeaderInfo';
import useSearchFilter from '../../../hooks/useSearchFilter/useSearchFilter';
import useWindowSize from '../../../hooks/useWindowSize';
import { useCampaignsInfiniteScroll, useItemsInfiniteScroll } from '../../../service/InventoryService';
import { fetchFulfillmentCentersRequest } from '../../../store/actions/fulfillmentCenters';
import { toggleDownloadSuccessModal } from '../../../store/actions/modals';
import { downloadReportsCSVRequest, fetchReportsRequest } from '../../../store/actions/reports';
import {
  selectFulfillmentCenterItems,
  selectFulfillmentCentersError,
  selectIsFulfillmentCentersFetching,
} from '../../../store/selectors/fulfillmentCenters';
import { selectIsProductMarketplaceEnabled } from '../../../store/selectors/organizations';
import {
  selectFilteredReports,
  selectIsDeliveryReportsFetching,
  selectIsDownloadingReportsCSV,
  selectReportsCount,
} from '../../../store/selectors/reports';
import { selectReportsActiveView } from '../../../store/selectors/shell';
import { ICampaign } from '../../../types/campaigns';
import { IInventoryItem } from '../../../types/inventories';
import { MARKETPLACE_FULFILLMENT_CENTER_ID, MARKETPLACE_FULFILLMENT_CENTER_NAME } from '../../../types/marketplace';
import {
  IDownloadReportsCSVRequestPayload,
  IReportsRequest,
  ReportSourceTypesEnum,
  ReportsRequestTypesEnum,
  ReportStatusEnum,
  ReportTypesEnum,
} from '../../../types/reports';
import {
  FilterTypesEnum,
  IApiError,
  IDatePickerOutput,
  IDatePickerRef,
  IDownloadCSVSuccessPayload,
  IDropdownWithContentOptions,
  NotificationListEnum,
} from '../../../types/shell';
import { IFilterItem, IFiltersRef } from '../../../types/tables';
import notification from '../../../utils/notification';
import Filters from './Filters';

import styles from '../ReportsContainer.module.scss';

interface IRenderDownloadDropDownProps {
  label: (options: IDropdownWithContentOptions) => React.ReactNode;
  contentClassName?: string;
}

interface IProps {
  currentPageSize: number;
}

const useFilters = ({ currentPageSize }: IProps) => {
  const dispatch = useDispatch();
  const { setAdditionalData: setHeaderAction } = useHeaderDispatch();
  const { mobile } = useWindowSize();

  const filtersRef = useRef<IFiltersRef>(null);

  const isDeliveryReportsFetching = useSelector(selectIsDeliveryReportsFetching);
  const isDownloadingReportsCSV = useSelector(selectIsDownloadingReportsCSV);

  const [tab, setTab] = useState<ReportTypesEnum>(ReportTypesEnum.Regular);
  const [startRange, setStartRange] = useState<IDatePickerOutput['from']>(null);
  const [endRange, setEndRange] = useState<IDatePickerOutput['to']>(null);
  const [sendType, setSendType] = useState<ReportSourceTypesEnum>(ReportSourceTypesEnum.Empty);
  const [status, setStatus] = useState<ReportStatusEnum | undefined | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const [selectedItemName, setSelectedItemName] = useState<string | undefined>(undefined);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(undefined);
  const [selectedCampaignName, setSelectedCampaignName] = useState<string | undefined>(undefined);
  const [selectedFulfillmentCenterIds, setSelectedFulfillmentCenterIds] = useState<string[]>([]);

  const fulfillmentCenters = useSelector(selectFulfillmentCenterItems);
  const isFulfillmentCentersFetching = useSelector(selectIsFulfillmentCentersFetching);
  const fulfillmentCentersError = useSelector(selectFulfillmentCentersError);
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);

  const dateRange = useMemo(() => ({ from: startRange || null, to: endRange || null }), [startRange, endRange]);

  const reports = useSelector(selectFilteredReports({ type: tab }));
  const reportsCount = useSelector(selectReportsCount);
  const activeView = useSelector(selectReportsActiveView);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const datePickerRef = useRef<IDatePickerRef>(null);

  const { SearchInput, query, resetSearch } = useSearchFilter({
    items: [],
    fieldNames: [],
    ExternalInput,
    props: { dataAction: 'delivery_reports_search' },
  });

  const filters = useMemo(
    () => ({
      query,
      tab,
      sendType,
      status,
      selectedDepartments,
      startRange,
      endRange,
      selectedItemId,
      selectedCampaignId,
      selectedFulfillmentCenterIds,
    }),
    [
      query,
      tab,
      sendType,
      status,
      selectedDepartments,
      startRange,
      endRange,
      selectedItemId,
      selectedCampaignId,
      selectedFulfillmentCenterIds,
    ],
  );

  // Wrapper function to make state setter compatible with IFilterItem onChange type
  const handleSendTypeChange = useCallback((value: unknown) => {
    setSendType(value as ReportSourceTypesEnum);
  }, []);

  const handleSetSelectedItemId = useCallback((id: string) => {
    setSelectedItemId(id);
    if (id) {
      // Clear campaign filter when inventory is selected
      setSelectedCampaignId(undefined);
      setSelectedCampaignName(undefined);
    }
  }, []);

  const handleSetSelectedCampaignId = useCallback((id: string) => {
    setSelectedCampaignId(id);
    if (id) {
      // Clear inventory filter when campaign is selected
      setSelectedItemId(undefined);
      setSelectedItemName(undefined);
    }
  }, []);

  const downloadReportsCSV = useCallback(
    (payload?: Partial<IDownloadReportsCSVRequestPayload>) => {
      // Mutual exclusivity: only send campaign_id OR item_id, not both
      const inventoryFilter = selectedCampaignId
        ? { campaign_id: selectedCampaignId }
        : selectedItemId
        ? { item_id: selectedItemId }
        : {};

      dispatch(
        downloadReportsCSVRequest({
          ...(selectedDepartments && selectedDepartments.length
            ? {
                department_ids: Array.isArray(selectedDepartments) ? selectedDepartments : [selectedDepartments],
              }
            : null),
          ...inventoryFilter,
          ...(sendType && { source_type: sendType }),
          ...(status && { status }),
          ...(selectedFulfillmentCenterIds.length ? { fulfillment_center_ids: selectedFulfillmentCenterIds } : null),
          type: tab,
          ...(startRange && endRange
            ? {
                date_range: {
                  from: startRange,
                  to: endRange,
                },
              }
            : null),
          ...(query && { search_query: query }),
          ...payload,
        }),
      );
    },
    [
      dispatch,
      selectedDepartments,
      sendType,
      status,
      tab,
      startRange,
      endRange,
      query,
      selectedItemId,
      selectedCampaignId,
      selectedFulfillmentCenterIds,
    ],
  );

  const fetchReports = useCallback(
    (payload: IReportsRequest) => {
      // Mutual exclusivity: only send campaign_id OR item_id, not both
      const inventoryFilter = selectedCampaignId
        ? { campaign_id: selectedCampaignId }
        : selectedItemId
        ? { item_id: selectedItemId }
        : {};

      return dispatch(
        fetchReportsRequest({
          body: {
            ...(selectedDepartments && selectedDepartments.length
              ? {
                  department_ids: Array.isArray(selectedDepartments) ? selectedDepartments : [selectedDepartments],
                }
              : null),
            ...inventoryFilter,
            ...(sendType && { source_type: sendType }),
            ...(status && { status }),
            ...(selectedFulfillmentCenterIds.length ? { fulfillment_center_ids: selectedFulfillmentCenterIds } : null),
            ...(startRange && endRange
              ? {
                  date_range: {
                    from: startRange,
                    to: endRange,
                  },
                }
              : null),
            ...(query && { search_query: query }),
            ...payload,
          },
          type: tab,
        }),
      );
    },
    [
      dispatch,
      selectedDepartments,
      sendType,
      status,
      startRange,
      endRange,
      query,
      tab,
      selectedItemId,
      selectedCampaignId,
      selectedFulfillmentCenterIds,
    ],
  );

  const handleDownloadClick = useCallback(
    (inventoryBreakdown: boolean) => {
      if (!startRange || !endRange) {
        notification.error(NotificationListEnum.DateRangeError, { content: 'Please, select dates' });
        datePickerRef?.current?.click();
        return;
      }

      const downloadReportsCSVPromise = new Promise<IDownloadCSVSuccessPayload>((resolve, reject) => {
        downloadReportsCSV({ resolve, reject, inventory_breakdown: inventoryBreakdown });
      }).then((response: IDownloadCSVSuccessPayload) => {
        dispatch(toggleDownloadSuccessModal(response));
      });

      notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
        promise: downloadReportsCSVPromise,
        promiseParams: {
          pending: "Downloading process started. We'll notify you when the file is ready!",
          error: {
            render: ({ data: error }) =>
              (error && error.message) || "Something bad has happened. CSV file wasn't created",
          },
        },
      });
    },
    [startRange, endRange, downloadReportsCSV, dispatch],
  );

  useEffect(() => {
    fetchReports({
      page: INITIAL_PAGE,
      page_size: currentPageSize,
    });
    // this array has to stay empty
  }, []);

  useEffect(() => {
    if (!fulfillmentCenters && !isFulfillmentCentersFetching && !fulfillmentCentersError) {
      dispatch(fetchFulfillmentCentersRequest());
    }
  }, [dispatch, fulfillmentCenters, isFulfillmentCentersFetching, fulfillmentCentersError]);

  const renderDownloadDropDownOptions = useCallback(
    () => [
      <button
        key={ReportsRequestTypesEnum.Campaign}
        onClick={() => handleDownloadClick(false)}
        className={styles.dropdownButton}
        data-action="download_campaign_report_csv"
      >
        Campaign Report
      </button>,
      <button
        key={ReportsRequestTypesEnum.Product}
        onClick={() => handleDownloadClick(true)}
        className={styles.dropdownButton}
        data-action="download_product_report_csv"
      >
        Product Report
      </button>,
    ],
    [handleDownloadClick],
  );

  const renderDownloadDropDown = useCallback(
    ({ label, contentClassName }: IRenderDownloadDropDownProps) => (
      <DropdownWithContent label={label} contentClassName={cn(styles.dropdownContent, contentClassName)}>
        {renderDownloadDropDownOptions}
      </DropdownWithContent>
    ),
    [renderDownloadDropDownOptions],
  );

  const setMobileHeader = useCallback(() => {
    if (!setHeaderAction) {
      return;
    }

    if (mobile) {
      const label = () => <TextIconButton icon={DownloadIcon} trigger />;

      setHeaderAction(
        isDownloadingReportsCSV ? (
          <AsyncButton loaderClassName={styles.spinner} disabled outlined className={styles.downloadBtn} isLoading />
        ) : (
          renderDownloadDropDown({
            label,
            contentClassName: styles.mobileContent,
          })
        ),
      );
    } else {
      setHeaderAction();
    }

    return setHeaderAction;
  }, [renderDownloadDropDown, mobile, setHeaderAction, isDownloadingReportsCSV]);

  useEffect(() => {
    return setMobileHeader();
  }, [setMobileHeader]);

  const handleSelectDateRange = useCallback(({ from: startDate, to: endDate }: IDatePickerOutput) => {
    setStartRange(startDate);
    setEndRange(endDate);
  }, []);

  const tabIndex = useMemo(() => Object.values(ReportTypesEnum).indexOf(tab as ReportTypesEnum), [tab]);

  const setTabIndex = useCallback(
    (index: number) => {
      setTab(Object.values(ReportTypesEnum)[index]);
      setSendType(ReportSourceTypesEnum.Empty);
      setStatus(null);
      setSelectedItemId(undefined);
      setSelectedItemName(undefined);
      setSelectedCampaignId(undefined);
      setSelectedCampaignName(undefined);
      setSelectedFulfillmentCenterIds([]);
      setSelectedDepartments([]);
      handleSelectDateRange({ from: null, to: null });
      resetSearch();
    },
    [handleSelectDateRange, resetSearch],
  );

  const handleSetSelectedFulfillmentCenterIds = useCallback((value: unknown) => {
    setSelectedFulfillmentCenterIds(Array.isArray(value) ? (value as string[]) : []);
  }, []);

  const isScheduledTab = useMemo(() => tab === ReportTypesEnum.Scheduled, [tab]);

  // Marketplace isn't a fulfillment center: add it for orgs that sell through it, never on Scheduled (no such sends).
  const warehouseOptions = useMemo(() => {
    const fulfillmentCenterOptions = (fulfillmentCenters ?? [])
      .filter((fc) => fc.uid !== NO_FULFILLMENT_CENTER && fc.uid !== MARKETPLACE_FULFILLMENT_CENTER_ID)
      .map((fc) => ({ label: fc.name, value: fc.uid }));

    if (!isMarketplaceEnabled || isScheduledTab) {
      return fulfillmentCenterOptions;
    }

    return [
      ...fulfillmentCenterOptions,
      { label: MARKETPLACE_FULFILLMENT_CENTER_NAME, value: MARKETPLACE_FULFILLMENT_CENTER_ID },
    ];
  }, [fulfillmentCenters, isMarketplaceEnabled, isScheduledTab]);

  const filterConfig = useMemo(() => {
    const statusOptions = isScheduledTab ? SCHEDULED_SEND_STATUSES : REGULAR_SEND_STATUSES;

    return [
      {
        label: 'Status',
        tabId: FilterTypesEnum.Status,
        value: status,
        onChange: setStatus,
        options: statusOptions.map(({ label, value }) => ({ label, value })),
        dataAction: 'status_filter',
      },
      {
        label: 'Campaign',
        tabId: FilterTypesEnum.Campaign,
        value: selectedCampaignId,
        onChange: handleSetSelectedCampaignId,
        useInfiniteScroll: true,
        isIconVisible: false,
        autocompleteConfig: {
          getId: ({ box_id }: ICampaign) => box_id,
          getName: ({ name }: ICampaign) => name,
          getImageUrl: ({ icon_url }: ICampaign) => icon_url,
          getSecondaryText: undefined,
          maxItems: MAX_CAMPAIGN_ITEMS_PER_PAGE,
        },
        dataHook: useCampaignsInfiniteScroll,
        options: [],
        dataAction: 'campaign_filter',
      },
      {
        label: 'Inventory',
        tabId: FilterTypesEnum.Item,
        value: selectedItemId,
        onChange: handleSetSelectedItemId,
        useInfiniteScroll: true,
        isIconVisible: true,
        autocompleteConfig: {
          getId: ({ item_id }: IInventoryItem) => item_id,
          getName: ({ name }: IInventoryItem) => name,
          getImageUrl: ({ image_url }: IInventoryItem) => image_url,
          getSecondaryText: ({ product_sku }: IInventoryItem) => product_sku,
          maxItems: MAX_INVENTORY_ITEMS_PER_PAGE,
        },
        dataHook: useItemsInfiniteScroll,
        options: [],
        dataAction: 'inventory_filter',
      },
      {
        label: 'Send type',
        tabId: FilterTypesEnum.Type,
        value: sendType,
        onChange: handleSendTypeChange,
        dataAction: 'send_type_filter',
        options: SEND_TYPES.map((t) => ({ label: t.title, value: t.value })),
        isMultiple: false,
        isVisible: true,
      },
      {
        label: 'Warehouse',
        tabId: FilterTypesEnum.Warehouse,
        value: selectedFulfillmentCenterIds,
        onChange: handleSetSelectedFulfillmentCenterIds,
        options: warehouseOptions,
        isMultiple: true,
        isVisible: warehouseOptions.length > 0,
        dataAction: 'warehouse_filter',
      },
    ];
  }, [
    selectedDepartments,
    sendType,
    status,
    setSelectedDepartments,
    handleSendTypeChange,
    selectedItemId,
    selectedCampaignId,
    handleSetSelectedItemId,
    handleSetSelectedCampaignId,
    selectedFulfillmentCenterIds,
    handleSetSelectedFulfillmentCenterIds,
    warehouseOptions,
    isScheduledTab,
  ]);

  const isAnyFilterActive = useMemo(() => {
    return (
      !!sendType ||
      dateRange?.from ||
      dateRange?.to ||
      (query && query?.length > 0) ||
      selectedDepartments.length > 0 ||
      selectedFulfillmentCenterIds.length > 0
    );
  }, [sendType, dateRange?.from, dateRange?.to, query, selectedDepartments, selectedFulfillmentCenterIds]);

  const handleClearAllFilters = useCallback(() => {
    setSendType(ReportSourceTypesEnum.Empty);
    setStatus(null);
    setSelectedItemId(undefined);
    setSelectedItemName(undefined);
    setSelectedCampaignId(undefined);
    setSelectedCampaignName(undefined);
    setSelectedFulfillmentCenterIds([]);
    setSelectedDepartments([]);

    handleSelectDateRange({
      from: null,
      to: null,
    });

    resetSearch();
  }, [handleSelectDateRange, resetSearch, setSelectedDepartments]);

  const isAnyFilterActiveRef = useRef(isAnyFilterActive);
  const handleClearAllFiltersRef = useRef(handleClearAllFilters);

  isAnyFilterActiveRef.current = isAnyFilterActive;
  handleClearAllFiltersRef.current = handleClearAllFilters;

  const downloadButton = useMemo(
    () =>
      renderDownloadDropDown({
        label: () => (
          <AsyncButton
            outlined
            disabled={isDownloadingReportsCSV}
            className={styles.downloadBtn}
            loaderClassName={styles.spinner}
            isLoading={isDownloadingReportsCSV}
            dataAction="download_reports_csv_button"
          >
            <DownloadIconV2 className={styles.icon} />
            <div>Download reports</div>
          </AsyncButton>
        ),
      }),
    [renderDownloadDropDown, isDownloadingReportsCSV],
  );

  const Component = useMemo(
    () => (
      <Filters
        activeTab={tabIndex}
        setActiveTab={setTabIndex}
        filterConfig={filterConfig as IFilterItem[]}
        ref={filtersRef}
        dateRange={dateRange}
        handleSelectDateRange={handleSelectDateRange}
        renderSearchField={SearchInput}
        query={query}
        datePickerRef={datePickerRef}
        renderDownloadButton={downloadButton}
        onSearchReset={resetSearch}
        selectedDepartments={selectedDepartments}
        handleSelectDepartments={setSelectedDepartments}
        selectedItemName={selectedItemName}
        setSelectedItemName={setSelectedItemName}
        selectedCampaignName={selectedCampaignName}
        setSelectedCampaignName={setSelectedCampaignName}
      />
    ),
    [
      SearchInput,
      tabIndex,
      setTabIndex,
      dateRange,
      handleSelectDateRange,
      renderDownloadDropDown,
      isDownloadingReportsCSV,
      resetSearch,
      selectedDepartments,
      setSelectedDepartments,
      filterConfig,
      selectedItemName,
      selectedCampaignName,
      query,
      downloadButton,
    ],
  );

  return {
    filteredReports: reports || [],
    filters,
    reportsCount,
    isDeliveryReportsFetching,
    filtersRef,
    onClear: handleClearAllFilters,
    Filters: Component,
    fetchReports,
    activeView,
  };
};

export default useFilters;
